<script lang="ts">
	// The home page, built from "Home Visibility" in the Claude Design project
	// (the design for the public site, decided 2026-10-03), from the design
	// system's components only. Where the design shows a feature the product
	// does not have yet, it says «قريبًا» (see copy.ts).

	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Chip from '../components/Chip.svelte';
	import Eyebrow from '../components/Eyebrow.svelte';
	import StatCard from '../components/StatCard.svelte';
	import AskDemo from './AskDemo.svelte';
	import { alerts, closing, compass, faq, features, hero, signUp, soon } from './copy';
	import { signUpHref } from './links';
	import OrdersDemo from './OrdersDemo.svelte';
	import ReportsDemo from './ReportsDemo.svelte';
	import SiteFooter from './SiteFooter.svelte';
	import SiteHeader from './SiteHeader.svelte';
	import SummaryDemo from './SummaryDemo.svelte';
</script>

<div class="page">
	<SiteHeader page="home" />

	<main>
		<section class="hero">
			<div>
				<div class="eyebrow"><Eyebrow text={hero.eyebrow} /></div>
				<h1>
					{hero.titleStart}<span class="mark">{hero.titleMark}</span>{hero.titleEnd}<br />
					{hero.titleLine2}
				</h1>
				<p class="lead">
					{hero.lead}<strong>{hero.leadStrong}</strong>{hero.leadEnd}
				</p>
				<div class="actions">
					<Button
						testId="site-hero-signup"
						tone="primary"
						size="lg"
						label={signUp}
						href={signUpHref}
					/>
					<Button
						testId="site-hero-report"
						tone="outline"
						size="md"
						label={hero.seeReport}
						href="#reports"
					/>
				</div>
			</div>
			<AskDemo />
		</section>

		<section class="section">
			<div class="compass">
				{#each compass as card (card.value)}
					<StatCard tone={card.tone} label={card.label} value={card.value}>{card.body}</StatCard>
				{/each}
			</div>
		</section>

		<ReportsDemo />

		<SummaryDemo />

		<section class="section">
			<div class="measure intro">
				<div class="kicker">
					<Eyebrow tone="ledger" text={alerts.eyebrow} />
					<Chip tone="flag" text={soon} />
				</div>
				<h2 class="h2">{alerts.title}</h2>
				<p class="copy">{alerts.body}</p>
			</div>
			<Card title={alerts.cardTitle} note={alerts.cardNote} testId="site-alerts">
				{#each alerts.items as alert (alert.title)}
					<div class="alert">
						<span class="dot" style:background={alert.dot}></span>
						<div class="alert-text">
							<p class="alert-title">{alert.title}</p>
							<p class="alert-body">{alert.body}</p>
						</div>
						<div class="alert-meta">
							<Chip tone={alert.tone} text={alert.tag} />
							<span class="figure time">{alert.time}</span>
						</div>
					</div>
				{/each}
			</Card>
		</section>

		<OrdersDemo />

		<section class="section">
			<h2 class="h2 features-title">{features.title}</h2>
			<div class="features">
				{#each features.items as feature (feature.title)}
					<Card pad>
						<div class="feature-head">
							<p class="feature-title">{feature.title}</p>
							{#if feature.soon}
								<Chip tone="flag" text={soon} />
							{/if}
						</div>
						<p class="feature-body">{feature.body}</p>
					</Card>
				{/each}
			</div>
		</section>

		<section id="faq" class="section">
			<div class="faq">
				<div>
					<div class="kicker"><Chip tone="flag" text={soon} /></div>
					<h2 class="h3">{faq.title}</h2>
					<p class="faq-lead">{faq.body}</p>
				</div>
				<div class="questions">
					{#each faq.items as item (item.q)}
						<div class="question">
							<p class="q">{item.q}</p>
							<p class="a">{item.a}</p>
						</div>
					{/each}
				</div>
			</div>
		</section>

		<section class="closing">
			<Card tone="invert" testId="site-closing">
				<div class="closing-body">
					<h2 class="h2">{closing.title}</h2>
					<p class="closing-copy">{closing.body}</p>
					<Button
						testId="site-closing-signup"
						tone="primary"
						size="lg"
						label={signUp}
						href={signUpHref}
					/>
				</div>
			</Card>
		</section>
	</main>

	<SiteFooter />
</div>

<style>
	.hero {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 380px), 1fr));
		gap: var(--space-12);
		align-items: center;
		padding-block: var(--section-y);
	}

	.eyebrow {
		margin-block-end: var(--space-8);
	}

	h1 {
		margin: 0;
		font-size: var(--text-h1);
		line-height: var(--leading-headline);
		font-weight: var(--weight-bold);
		letter-spacing: var(--tracking-tight);
		text-wrap: pretty;
	}

	.mark {
		display: inline-block;
		border-block-end: var(--underline-weight) solid var(--ledger);
		padding-block-end: 3px;
	}

	.lead {
		margin: var(--space-10) 0 0;
		max-inline-size: var(--measure);
		font-size: var(--text-lead);
		line-height: var(--leading-body);
		color: var(--text-body);
		text-wrap: pretty;
	}

	.lead strong {
		color: var(--text-strong);
		font-weight: var(--weight-semibold);
	}

	.actions {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-5);
		align-items: center;
		margin-block-start: var(--space-11);
	}

	.compass {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr));
		gap: var(--grid-gap);
	}

	.intro {
		margin-block-end: var(--space-10);
	}

	.alert {
		display: grid;
		grid-template-columns: 7px minmax(0, 1fr) auto;
		gap: var(--space-6);
		align-items: center;
		padding: var(--row-pad);
		border-block-end: var(--border-width) solid var(--border-inset);
	}

	.dot {
		inline-size: 7px;
		block-size: 7px;
		border-radius: var(--radius-pill);
	}

	.alert-text {
		min-inline-size: 0;
	}

	.alert-title {
		margin: 0 0 2px;
		font-size: var(--text-xs);
		font-weight: var(--weight-semibold);
	}

	.alert-body {
		margin: 0;
		font-size: var(--text-2xs);
		line-height: var(--leading-snug);
		color: var(--text-body);
	}

	.alert-meta {
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		gap: var(--space-2);
	}

	.time {
		font-size: var(--text-micro);
		color: var(--text-muted);
	}

	.features-title {
		margin-block-end: var(--space-10);
	}

	.features {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 272px), 1fr));
		gap: var(--grid-gap);
	}

	.feature-head {
		display: flex;
		justify-content: space-between;
		align-items: baseline;
		gap: var(--space-4);
		margin-block-end: var(--space-3);
	}

	.feature-title {
		margin: 0;
		font-size: var(--text-md);
		font-weight: var(--weight-semibold);
	}

	.feature-body {
		margin: 0;
		font-size: var(--text-xs);
		line-height: var(--leading-snug);
		color: var(--text-body);
	}

	.faq {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 300px), 1fr));
		gap: var(--space-12);
	}

	.h3 {
		margin: 0 0 var(--space-4);
		font-size: var(--text-h3);
		font-weight: var(--weight-bold);
		letter-spacing: var(--tracking-snug);
		line-height: var(--leading-headline);
	}

	.faq-lead {
		margin: 0;
		font-size: var(--text-body-sm);
		line-height: var(--leading-body);
		color: var(--text-body);
	}

	.questions {
		display: flex;
		flex-direction: column;
	}

	.question {
		padding-block: var(--space-6);
		border-block-start: var(--border-width) solid var(--border-default);
	}

	.q {
		margin: 0 0 var(--space-2);
		font-size: var(--text-md);
		font-weight: var(--weight-semibold);
	}

	.a {
		margin: 0;
		font-size: var(--text-sm);
		line-height: var(--leading-body);
		color: var(--text-body);
	}

	.closing {
		padding-block: var(--space-11) var(--section-y);
		border-block-start: var(--border-width) solid var(--border-default);
	}

	.closing-body {
		padding: clamp(24px, 4vw, 40px);
	}

	.closing-copy {
		margin: 0 0 var(--space-10);
		max-inline-size: var(--measure);
		font-size: var(--text-base);
		line-height: var(--leading-body);
		color: var(--on-ink-body);
	}
</style>
